import type { ChartDataPoint, CsDashboardFeatureStats, MergedAgentRow } from './types';

type CsAgentRosterNode = {
  Email: string;
  agentId?: string;
  subordinates?: CsAgentRosterNode[];
};

const EXCLUDED_AGENT_EMAILS = new Set(['external+prod-sierra@luxgroup.com']);

export function isExcludedAgentEmail(email: string | null | undefined): boolean {
  return EXCLUDED_AGENT_EMAILS.has((email ?? '').toLowerCase());
}

// `yyyymmdd` is the format svc-reporting returns dates in (e.g. `20260514`);
// The dashboard only ever uses these as grouping keys or for display formatting — never for date
// arithmetic — so we keep them as strings end-to-end.
export function formatJsonDate(yyyymmdd: string): string {
  if (yyyymmdd.length !== 8) return yyyymmdd;
  return `${yyyymmdd.slice(6, 8)}/${yyyymmdd.slice(4, 6)}`;
}

/**
 * Build chart points from the server-supplied per-feature `dailyTotals`.
 * No SUM in JS — svc-reporting already aggregated across agents.
 * Not retiring this function as it will be used again when filtering logic
 * is moved server-side. buildChartDataFromAgentRows was a temp patch for showcase.
 **/
export function buildChartDataFromServerTotals(
  features: CsDashboardFeatureStats[] | undefined,
  featureIds: string[],
): ChartDataPoint[] {
  if (!features) return [];

  const dateSet = new Set<string>();
  features.forEach((feature) => {
    feature.dailyTotals.forEach(({ date }) => dateSet.add(date));
  });

  const dates = Array.from(dateSet).sort();
  const totalsByFeature = new Map(
    features.map((feature) => [
      feature.featureId,
      new Map(feature.dailyTotals.map((dailyTotal) => [dailyTotal.date, dailyTotal.count])),
    ]),
  );

  return dates.map((date) => {
    const point: ChartDataPoint = { date };
    featureIds.forEach((featureId) => {
      point[featureId] = totalsByFeature.get(featureId)?.get(date) ?? 0;
    });
    return point;
  });
}

export function buildChartDataFromAgentRows(featureIds: string[], agentRows: MergedAgentRow[]): ChartDataPoint[] {
  const dateSet = new Set<string>();
  agentRows.forEach((row) => {
    featureIds.forEach((featureId) => {
      Object.keys(row.countsByFeatureAndDate[featureId] ?? {}).forEach((date) => dateSet.add(date));
    });
  });

  const dates = Array.from(dateSet).sort();
  return dates.map((date) => {
    const point: ChartDataPoint = { date };
    featureIds.forEach((featureId) => {
      point[featureId] = agentRows.reduce(
        (total, row) => total + (row.countsByFeatureAndDate[featureId]?.[date] ?? 0),
        0,
      );
    });
    return point;
  });
}

/**
 * When a team leader filter is active, the server's all-agent totals don't
 * apply — we have to re-sum from the per-agent rows for just the subordinate
 * agents. This is the only place the frontend does SUM aggregation, and it
 * exists because the CS agent hierarchy (csAgents.json) lives client-side.
 * When the hierarchy ever moves into a service, the filter param should be
 * passed to svc-reporting and this branch can be deleted.
 */
export function buildChartDataForFilteredTeam(
  featureIds: string[],
  agentRows: MergedAgentRow[],
  filteredAgentIds: Set<string>,
): ChartDataPoint[] {
  const dateSet = new Set<string>();
  agentRows.forEach((row) => {
    featureIds.forEach((featureId) => {
      Object.keys(row.countsByFeatureAndDate[featureId] ?? {}).forEach((date) => dateSet.add(date));
    });
  });

  const dates = Array.from(dateSet).sort();
  return dates.map((date) => {
    const point: ChartDataPoint = { date };
    featureIds.forEach((featureId) => {
      let total = 0;
      agentRows.forEach((row) => {
        if (!filteredAgentIds.has(row.agentId)) return;
        total += row.countsByFeatureAndDate[featureId]?.[date] ?? 0;
      });
      point[featureId] = total;
    });
    return point;
  });
}

export function buildCsAgentRosterRows(agents: CsAgentRosterNode[]): MergedAgentRow[] {
  const map = new Map<string, MergedAgentRow>();

  function addAgent(agent: CsAgentRosterNode) {
    const agentEmail = agent.Email.trim();
    if (!agentEmail || isExcludedAgentEmail(agentEmail)) return;

    const agentId = agent.agentId || agentEmail.toLowerCase();
    if (!map.has(agentId)) {
      map.set(agentId, { agentId, agentEmail, countsByFeatureAndDate: {} });
    }
  }

  function collectSubordinates(agent: CsAgentRosterNode) {
    agent.subordinates?.forEach((subordinate) => {
      addAgent(subordinate);
      collectSubordinates(subordinate);
    });
  }

  agents.forEach(collectSubordinates);

  return Array.from(map.values()).sort((a, b) => (a.agentEmail ?? a.agentId).localeCompare(b.agentEmail ?? b.agentId));
}

export function buildMergedAgentRows(
  features: CsDashboardFeatureStats[] | undefined,
  rosterRows?: MergedAgentRow[],
): MergedAgentRow[] {
  const map = new Map<string, MergedAgentRow>();
  const agentIdByEmail = new Map<string, string>();
  const hasRoster = rosterRows !== undefined;

  rosterRows?.forEach((row) => {
    map.set(row.agentId, { ...row, countsByFeatureAndDate: {} });
    if (row.agentEmail) {
      agentIdByEmail.set(row.agentEmail.toLowerCase(), row.agentId);
    }
  });

  features?.forEach(({ featureId, agents }) => {
    agents.forEach(({ agentId, agentEmail, dailyCounts }) => {
      if (isExcludedAgentEmail(agentEmail)) return;

      const rowAgentId = agentIdByEmail.get((agentEmail ?? '').toLowerCase()) ?? agentId;
      if (!map.has(rowAgentId)) {
        if (hasRoster) return;
        map.set(rowAgentId, { agentId: rowAgentId, agentEmail, countsByFeatureAndDate: {} });
      }
      const row = map.get(rowAgentId)!;
      row.agentEmail = row.agentEmail ?? agentEmail;
      row.countsByFeatureAndDate[featureId] = {};
      dailyCounts.forEach(({ date, count }) => {
        row.countsByFeatureAndDate[featureId][date] = count;
      });
    });
  });

  return Array.from(map.values()).sort((a, b) => (a.agentEmail ?? a.agentId).localeCompare(b.agentEmail ?? b.agentId));
}

export function getAllDates(rows: MergedAgentRow[], featureIds: string[]): string[] {
  const dateSet = new Set<string>();
  rows.forEach((row) => {
    featureIds.forEach((featureId) => {
      Object.keys(row.countsByFeatureAndDate[featureId] ?? {}).forEach((date) => dateSet.add(date));
    });
  });
  return Array.from(dateSet).sort();
}

export function computeMaxCellCount(rows: MergedAgentRow[], featureIds: string[], dates: string[]): number {
  let max = 0;
  rows.forEach((row) => {
    featureIds.forEach((featureId) => {
      const byDate = row.countsByFeatureAndDate[featureId] ?? {};
      dates.forEach((date) => {
        const c = byDate[date] ?? 0;
        if (c > max) max = c;
      });
    });
  });
  return max;
}
